const EventBus = window.EventBus;

/**
 * LBT-1461 — Horizontal "Popular Features" quick-filter bar for the SRP.
 *
 * Renders the SAME feature set as the vertical filter's "Popular Features"
 * group (component="feature", :items="options.features"). It is meant to be
 * used INSIDE the same <new-advanced-search> instance as the vertical filter,
 * so it shares the parent's reactive `selected.feature` via the existing
 * updateChecked/updateTags contract — driving filtering + URL with no
 * cross-instance plumbing (see Fase 0 spike decision: Option A).
 *
 * Sibling display sync: the bar and the vertical group keep independent local
 * `selected` copies, so a `SYNC_FEATURE_SELECTION` EventBus event mirrors the
 * active state between them (the bar emits + listens here; the vertical
 * `advanced-list-w-view-more` gets the matching listener in Fase 3).
 */
export const FeatureQuickBar = {
  template: `
  <div v-show="hasItems" class="feature-quick-bar w-full relative flex items-center">
    <button
      type="button"
      v-show="canScrollLeft"
      @click="scroll(-1)"
      class="fqb-arrow fqb-arrow-left mr-2 flex items-center justify-center shrink-0 w-9 h-9 rounded-full border border-gray-300 bg-white cursor-pointer hover:border-primary"
      :aria-label="$t('Scroll left')">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M15 6L9 12L15 18" stroke="#39393C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>

    <div ref="track" @scroll="updateArrows" class="fqb-track flex-1 min-w-0 flex items-center gap-3 overflow-x-auto scroll-smooth py-2">
      <button
        v-for="item in displayItems"
        :key="item"
        type="button"
        @click="toggle(component, item)"
        :title="$t(item)"
        :class="['fqb-chip flex items-center gap-2 shrink-0 px-3 py-2 rounded-lg border cursor-pointer transition-all select-none', selected.includes(item) ? 'fqb-chip-active border-primary text-primary' : 'border-gray-200 text-dark hover:border-primary']">
        <img class="fqb-icon h-6 w-6 object-contain" :src="iconPath(item)" :alt="$t(item)" @error="onIconError($event)">
        <span class="fqb-label text-sm font-antenna-cond-bold whitespace-nowrap capitalize">{{ $t(item) }}<span v-if="showCounts" class="fqb-count ml-1 text-xs opacity-70">{{ getCount(item) }}</span></span>
      </button>
    </div>

    <button
      type="button"
      v-show="canScrollRight"
      @click="scroll(1)"
      class="fqb-arrow fqb-arrow-right ml-2 flex items-center justify-center shrink-0 w-9 h-9 rounded-full border border-gray-300 bg-white cursor-pointer hover:border-primary"
      :aria-label="$t('Scroll right')">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9 6L15 12L9 18" stroke="#39393C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>
  </div>
  `,
  props: [
    "component",
    "title",
    "items",
    "initialSelection",
    "optionsCount",
    "showCounts",
    "fallbackIcon",
    "availableIcons",
    "slotId",
  ],
  data: function () {
    return {
      theme_directory: window.theme_directory,
      selected: this.initialSelection ? this.initialSelection.slice() : [],
      loaded: false,
      // Last non-empty feature set, so the bar stays usable when the current
      // selection yields zero results (options collapse to empty) and the user
      // can still deselect a chip to recover instead of being trapped.
      lastItems: [],
      canScrollLeft: false,
      canScrollRight: false,
    };
  },
  mounted() {
    // Clear flow shared with the rest of the filter.
    EventBus.$on("CLEAR_ALL_NEW_TOGGLE_LIST_ABSOLUTE_ADVANCED_SELECTION", () => {
      this.selected = [];
    });
    // Sibling display sync (bar <-> vertical "Popular Features" group).
    EventBus.$on("SYNC_FEATURE_SELECTION", (payload) => {
      if (!payload || payload.source === this._uid) return;
      this.selected = Array.isArray(payload.selected) ? payload.selected.slice() : [];
    });

    // Relocate the bar above the listing (the component is rendered inside the
    // sidebar filter instance so it shares state; see Fase 0 spike / Option A).
    // spa-inventory uses inline-template and may (re)build its DOM after our
    // mount, so relocate idempotently with a couple of deferred retries to land
    // in — and stay in — the final slot node.
    this.$nextTick(() => { this.relocate(); this.updateArrows(); });
    setTimeout(() => { this.relocate(); this.updateArrows(); }, 0);
    setTimeout(() => { this.relocate(); this.updateArrows(); }, 300);
    window.addEventListener("resize", this.updateArrows);
    // NOTE: we intentionally do NOT emit initial `updatetags` here — the vertical
    // "Popular Features" component already emits the initial tags on mount, so
    // emitting again would create duplicate tags for the same initialSelection.
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateArrows);
  },
  methods: {
    slugify(item) {
      return String(item).toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
    },
    iconPath(item) {
      const slug = this.slugify(item);
      // Skip the request entirely when the icon list says the file is missing.
      if (Array.isArray(this.availableIcons) && !this.availableIcons.includes(slug)) {
        const fallback = this.fallbackIcon || "features/feature-default.svg";
        return `${this.theme_directory}/resources/images/${fallback}`;
      }
      return `${this.theme_directory}/resources/images/features/${slug}.svg`;
    },
    onIconError(event) {
      const fallback = this.fallbackIcon || "features/feature-default.svg";
      const url = `${this.theme_directory}/resources/images/${fallback}`;
      // Guard against an infinite error loop if the fallback itself is missing.
      if (event.target.getAttribute("src") !== url) {
        event.target.src = url;
      } else {
        event.target.style.visibility = "hidden";
      }
    },
    getCount(item) {
      if (this.optionsCount && this.optionsCount[item]) return `(${this.optionsCount[item]})`;
      return "";
    },
    entryPoint(items) {
      // Reconcile the URL-derived initialSelection (slugified) with the real
      // feature labels coming from inventory — same logic as advanced-list-w-view-more.
      this.selected = items.filter((i) => {
        return this.initialSelection.includes(
          i.replace(/ /g, "-").replace(/---/g, "-").toLowerCase()
        );
      });
    },
    toggle(component, item) {
      let enabled = true;
      if (this.selected.includes(item)) {
        enabled = false;
        this.selected.splice(this.selected.indexOf(item), 1);
      } else {
        this.selected.push(item);
      }

      // 1) Drive filtering + URL through the parent <new-advanced-search>.
      this.$emit("updatechecked", { component, selected: this.selected });
      this.$emit("updatetags", { component, selected: item, status: enabled });

      // 2) Mirror the active state to the sibling vertical group.
      EventBus.$emit("SYNC_FEATURE_SELECTION", {
        source: this._uid,
        selected: this.selected.slice(),
      });
    },
    relocate() {
      if (!this.slotId) return;
      const slot = document.getElementById(this.slotId);
      if (slot && this.$el && slot !== this.$el.parentNode) {
        slot.appendChild(this.$el);
      }
    },
    scroll(direction) {
      const track = this.$refs.track;
      if (!track) return;
      const chips = track.querySelectorAll(".fqb-chip");
      const maxScroll = track.scrollWidth - track.clientWidth;
      const view = track.clientWidth;
      const current = track.scrollLeft;
      const EPS = 1;

      // No chips measured yet: fall back to a simple paged scroll.
      if (!chips.length) {
        track.scrollBy({ left: direction * Math.round(view * 0.8), behavior: "smooth" });
        return;
      }

      // Each chip's left/right edge in scroll-content coordinates (i.e. the
      // scrollLeft value that would left-align that chip). Computed via
      // getBoundingClientRect so it's independent of the offsetParent.
      const trackLeft = track.getBoundingClientRect().left;
      const edges = Array.prototype.map.call(chips, (c) => {
        const r = c.getBoundingClientRect();
        const start = r.left - trackLeft + current;
        return { start: start, end: start + r.width };
      });

      let target;
      if (direction > 0) {
        // Snap so the first chip that isn't fully visible on the right becomes
        // left-aligned — reveal the next partially-hidden/hidden chip on a chip
        // boundary instead of a fixed pixel jump.
        const visibleRight = current + view;
        const hidden = edges.find((e) => e.end > visibleRight + EPS);
        if (!hidden) {
          target = maxScroll;
        } else if (hidden.start > current + EPS) {
          target = hidden.start;
        } else {
          // Chip already left-aligned but wider than the viewport (can't be
          // shown whole): advance to the next chip so we never get stuck.
          const next = edges.find((e) => e.start > current + EPS);
          target = next ? next.start : maxScroll;
        }
      } else {
        // Mirror: bring the last chip hidden on the left into view, landing on a
        // chip boundary.
        let hidden = null;
        for (let i = edges.length - 1; i >= 0; i--) {
          if (edges[i].start < current - EPS) { hidden = edges[i]; break; }
        }
        if (!hidden) {
          target = 0;
        } else {
          const wanted = hidden.end - view;
          const snapped = edges.filter((e) => e.start <= wanted + EPS);
          target = snapped.length ? snapped[snapped.length - 1].start : 0;
        }
      }

      // Guarantee forward/backward progress (e.g. degenerate measurements).
      if (direction > 0 && target <= current + EPS) target = Math.min(maxScroll, current + Math.round(view * 0.8));
      if (direction < 0 && target >= current - EPS) target = Math.max(0, current - Math.round(view * 0.8));

      target = Math.max(0, Math.min(target, maxScroll));
      track.scrollTo({ left: target, behavior: "smooth" });
    },
    updateArrows() {
      const track = this.$refs.track;
      if (!track) {
        this.canScrollLeft = false;
        this.canScrollRight = false;
        return;
      }
      const maxScroll = track.scrollWidth - track.clientWidth;
      this.canScrollLeft = track.scrollLeft > 1;
      this.canScrollRight = track.scrollLeft < maxScroll - 1;
    },
  },
  watch: {
    items(items) {
      if (!this.loaded) {
        this.entryPoint(items);
        this.loaded = true;
      }
      // Remember the last non-empty set for the zero-result fallback below.
      if (items && items.length) this.lastItems = items.slice();
      this.$nextTick(this.updateArrows);
    },
  },
  computed: {
    displayItems() {
      // Normal case: render the live feature set.
      if (this.items && this.items.length) return this.items;
      // Zero-result case: options collapse to empty. Keep the bar visible with
      // the last known chips while a selection is active, so the active chips
      // can be deselected to recover (otherwise the bar would vanish until a
      // full "Clear Filters"). With nothing selected there is genuinely nothing
      // to show, so stay hidden.
      if (this.selected.length) return this.lastItems;
      return [];
    },
    hasItems() {
      return this.displayItems.length > 0;
    },
  },
};
